import { cleanFilters, type FiltersState } from "@investhinks/api-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type StrapiFilters = FiltersState;

export interface UseStrapiFiltersReturn {
  filters: StrapiFilters;
  setFilters: (filters: StrapiFilters | ((prev: StrapiFilters) => StrapiFilters)) => void;
  changeFilter: (key: string, value: unknown) => void;
  resetFilters: () => void;
  filterParams: StrapiFilters | undefined;
}

/**
 * Hook for managing Strapi filter state.
 *
 * Tracks user modifications separately from initialFilters to ensure:
 * 1. When initialFilters changes, filterParams updates synchronously (no double-load)
 * 2. User modifications are preserved until explicitly reset or initialFilters changes
 */
const useStrapiFilters = (initialFilters: StrapiFilters = {}): UseStrapiFiltersReturn => {
  // Track user modifications separately - null means no modifications (use initialFilters)
  const [userModifications, setUserModifications] = useState<StrapiFilters | null>(null);

  // Stabilize initialFilters to prevent unnecessary re-renders
  const initialFiltersKey = JSON.stringify(initialFilters);
  const stableInitialFilters = useMemo(() => initialFilters, [initialFiltersKey]);

  // Track previous initialFilters key to detect changes synchronously during render
  const prevInitialFiltersKeyRef = useRef(initialFiltersKey);
  const initialFiltersChanged = prevInitialFiltersKeyRef.current !== initialFiltersKey;

  // Compute effective filters synchronously
  // When initialFilters changes, this immediately returns the new value
  // (userModifications is ignored when initialFilters just changed)
  const filters = useMemo(() => {
    // If initialFilters just changed this render, ignore old modifications
    if (initialFiltersChanged || userModifications === null) {
      return stableInitialFilters;
    }
    // Merge initial filters with user modifications
    // User modifications override initial filters for the same key
    return { ...stableInitialFilters, ...userModifications };
  }, [stableInitialFilters, userModifications, initialFiltersChanged]);

  // Update the ref after computing filters (so next render sees the update)
  // and clear stale modifications via effect (proper React pattern)
  useEffect(() => {
    if (initialFiltersChanged) {
      prevInitialFiltersKeyRef.current = initialFiltersKey;
      // Clear stale user modifications now that initialFilters changed
      if (userModifications !== null) {
        setUserModifications(null);
      }
    }
  }, [initialFiltersKey, initialFiltersChanged, userModifications]);

  const setFilters = useCallback((newFilters: StrapiFilters | ((prev: StrapiFilters) => StrapiFilters)) => {
    setUserModifications((prev) => {
      const currentFilters = prev ?? {};
      const computed = typeof newFilters === "function" ? newFilters(currentFilters) : newFilters;
      return computed;
    });
  }, []);

  const changeFilter = useCallback((key: string, value: unknown) => {
    setUserModifications((prev) => {
      const currentMods = prev ?? {};
      if (value === undefined || value === null || value === "") {
        const { [key]: _, ...rest } = currentMods;
        return Object.keys(rest).length > 0 ? rest : null;
      }
      return { ...currentMods, [key]: value };
    });
  }, []);

  // Reset clears user modifications, reverting to current initialFilters
  const resetFilters = useCallback(() => {
    setUserModifications(null);
  }, []);

  // Remove empty/null/undefined values for clean API params
  const filterParams = cleanFilters(filters);

  return {
    filters,
    setFilters,
    changeFilter,
    resetFilters,
    filterParams,
  };
};

export default useStrapiFilters;
